"use client";

import { useEffect, useRef } from "react";
import type { PieceSymbol } from "chess.js";
import { PIECE_LABEL } from "./interaction";
import { PieceArt } from "./PieceArt";
import { useAppearance } from "@/components/site/AppearanceProvider";

const CHOICES: PieceSymbol[] = ["q", "r", "b", "n"];

/** Modal piece chooser, shared by the 2D and 3D boards. Escape cancels. */
export function PromotionPicker({ color, onChoose }: { color: "w" | "b"; onChoose: (p: PieceSymbol | null) => void }) {
  const { appearance } = useAppearance();
  const first = useRef<HTMLButtonElement>(null);
  // Parents often pass a new callback each render; a ref keeps focus from jumping back to Queen.
  const choose = useRef(onChoose);
  useEffect(() => {
    choose.current = onChoose;
  }, [onChoose]);
  useEffect(() => {
    // Return focus to the board square that opened the picker.
    const opener = document.activeElement as HTMLElement | null;
    first.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") choose.current(null);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  return (
    <div className="absolute inset-0 z-30 grid place-items-center bg-black/40" role="dialog" aria-modal="true" aria-label="Choose a promotion piece">
      <div className="rounded-xl border border-border bg-surface p-3 shadow-xl">
        <p className="mb-2 text-center text-sm font-medium">Promote to</p>
        <div className="flex gap-2">
          {CHOICES.map((p, i) => (
            <button
              key={p}
              ref={i === 0 ? first : undefined}
              onClick={() => onChoose(p)}
              className="grid h-16 w-16 place-items-center rounded-lg bg-surface-2 hover:bg-accent-soft"
              aria-label={PIECE_LABEL[p]}
            >
              <span className="h-14 w-14"><PieceArt type={p} color={color} finish={appearance.pieces} style={appearance.style}/></span>
            </button>
          ))}
        </div>
        <button onClick={() => onChoose(null)} className="mt-2 w-full text-xs text-muted hover:text-text">
          Cancel
        </button>
      </div>
    </div>
  );
}
